import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useDeviceSelection } from '../context/DeviceSelectionContext';
import {
  Box,
  Typography,
  Alert,
  Button,
  LinearProgress,
  ButtonGroup,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Paper,
  Stack,
  Grid,
  Chip,
  Divider
} from '@mui/material';
import { 
  Send as SendIcon,
  FileDownload as DownloadIcon,
  FileUpload as UploadIcon
} from '@mui/icons-material';
import api from '../api/axios';
import { getSocket } from '../socket';
import { useAuth } from '../auth/AuthContext';
import SortableTable from '../components/SortableTable';
import DeviceFeedbackTable from '../components/DeviceFeedbackTable';
import { 
  exportComprehensiveData, 
  importComprehensiveData, 
  createComprehensiveTemplate
} from '../utils/excelUtils';

// Titled surface matching the rest of the app (mirrors Dashboard's SectionCard) so
// Device Control reads as one system with the other pages.
const SectionCard = ({ title, action, children, sx }) => (
  <Paper sx={{ p: 2.25, height: '100%', display: 'flex', flexDirection: 'column', ...sx }}>
    {(title || action) && (
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5, gap: 1, flexWrap: 'wrap' }}>
        {title ? <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{title}</Typography> : <span />}
        {action}
      </Stack>
    )}
    {children}
  </Paper>
);

const DeviceControl = () => {
  const { selectedDeviceId } = useDeviceSelection();
  const { setSelectedDeviceId } = useDeviceSelection();
  const { role } = useAuth();
  const [deviceSettings, setDeviceSettings] = useState({});
  const [elementChecklist, setElementChecklist] = useState([]);
  const [alert, setAlert] = useState(null);
  const [uiLogs, setUiLogs] = useState([]); // lightweight console log for last 5 UI events
  const [loading, setLoading] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const VOUT_VIS_KEY = 'allowUserVoutView';
  const [allowUserVoutView, setAllowUserVoutView] = useState(() => {
    const stored = localStorage.getItem(VOUT_VIS_KEY);
    if (stored === null || stored === undefined) return true;
    return stored === 'true';
  });

  const fileInputRef = useRef(null);
  const [transportMode, setTransportMode] = useState('auto');
  const [transportLoading, setTransportLoading] = useState(false);
  const [deviceOptions, setDeviceOptions] = useState([]);
  const [otaStatus, setOtaStatus] = useState({ progress: null, message: '', deviceId: null, target: '' });
  const [elementProgress, setElementProgress] = useState({ processed: null, total: null, symbol: null, ts: null, deviceId: null });
  const addLog = (message, type = 'info') => {
    setUiLogs(prev => [{ message, type, timestamp: new Date().toLocaleTimeString() }, ...prev].slice(0, 5));
  };

  // Safe progress display values so the bar is always visible for the selected device
  const progressProcessed = Number.isFinite(elementProgress?.processed) ? elementProgress.processed : 0;
  const progressTotal = Number.isFinite(elementProgress?.total) ? elementProgress.total : 0;
  const progressValue = progressTotal > 0
    ? Math.min(100, Math.max(0, (progressProcessed / progressTotal) * 100))
    : 0;
  const progressLabel = (elementProgress?.processed !== null || elementProgress?.total !== null)
    ? `${progressProcessed}/${progressTotal}${elementProgress?.symbol ? ` (${elementProgress.symbol})` : ''}`
    : 'No progress yet';
  // Fetch current transport mode on mount
  useEffect(() => {
    const fetchTransportMode = async () => {
      try {
  const response = await api.get('/device/transport-mode');
        setTransportMode(response.data.mode || 'auto');
        addLog(`Transport mode is ${response.data.mode || 'auto'}`, 'info');
      } catch (error) {
        setAlert({ type: 'error', message: 'Failed to fetch transport mode' });
        addLog('Failed to fetch transport mode', 'error');
      }
    };
    const fetchDevices = async () => {
      try {
        const res = await api.get('/device/devices/registered');
        const list = Array.isArray(res.data) ? res.data : [];
        const ids = list.map(d => ({
          id: d.deviceId,
          name: d.customName || d.deviceId,
          stmFirmwareVersion: d.stmFirmwareVersion || null,
          buildCode: d.buildCode != null ? d.buildCode : null,
          espBridgeVersion: d.espBridgeVersion || null,
        }));
        setDeviceOptions(ids);
      } catch (e) {
        // Fallback: try online devices
        try {
          const res = await api.get('/device/devices/online');
          const list = Array.isArray(res.data) ? res.data : [];
          const ids = list.map(d => (typeof d === 'string' ? { id: d, name: d } : { id: d.deviceId || d, name: d.deviceId || d }));
          setDeviceOptions(ids);
        } catch (err) {
          setDeviceOptions([]);
        }
      }
    };
    fetchTransportMode();
    fetchDevices();
  }, []);

  useEffect(() => {
    const handleStorage = () => {
      const stored = localStorage.getItem(VOUT_VIS_KEY);
      setAllowUserVoutView(stored === null || stored === undefined ? true : stored === 'true');
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  // Change transport mode
  const handleTransportModeChange = async (mode) => {
    setTransportLoading(true);
    try {
  const response = await api.post('/device/transport-mode', { mode });
      setTransportMode(response.data.mode);
      setAlert({ type: 'success', message: `Transport mode set to ${response.data.mode}` });
      addLog(`Transport mode set to ${response.data.mode}`, 'success');
    } catch (error) {
      setAlert({ type: 'error', message: 'Failed to set transport mode' });
      addLog('Failed to set transport mode', 'error');
    }
    setTransportLoading(false);
  };
  // Remove local deviceId state, use selectedDeviceId from props
  // Feedback/ADC/statistics state
  const [feedbackInfo, setFeedbackInfo] = useState(null);
// ...existing code...

  // Get deviceId from socket events
  useEffect(() => {
    const socket = getSocket();
    // Listen for device-feedback events (new event from backend)
    socket.on('device-feedback', (data) => {
      if (!data) return;
      // Only accept feedback for the currently selected device
      if (selectedDeviceId && data.deviceId && data.deviceId !== selectedDeviceId) return;
      setFeedbackInfo(data);
    });
    socket.on('device-ack', (ack) => {
      if (ack && ack.ack) {
        setAlert({ type: 'success', message: ack.message || 'ACK_OK' });
      } else if (ack) {
        setAlert({ type: 'error', message: `ACK error ${ack.status}: ${ack.message || 'Unknown error'}` });
      }
    });
    socket.on('ota-status', (msg) => {
      if (!msg || !msg.deviceId) return;
      if (selectedDeviceId && msg.deviceId !== selectedDeviceId) return;
      const progressVal = typeof msg.progress === 'number' ? Math.max(0, Math.min(100, msg.progress)) : null;
      setOtaStatus({
        progress: progressVal,
        message: msg.message || msg.otaStatus || 'Process running',
        deviceId: msg.deviceId,
        target: msg.target || msg.otaTarget || 'stm32'
      });
      if (msg.otaStatus && ['success', 'failed', 'error'].includes(String(msg.otaStatus).toLowerCase())) {
        setTimeout(() => setOtaStatus(prev => ({ ...prev, progress: null })), 5000);
      }
    });
    socket.on('element-progress', (msg) => {
      if (!msg || !msg.deviceId) return;
      if (selectedDeviceId && msg.deviceId !== selectedDeviceId) return;
      const total = Number.isFinite(Number(msg.total)) ? Number(msg.total) : null;
      const processed = Number.isFinite(Number(msg.processed)) ? Number(msg.processed) : null;

      // Keep a breadcrumb in the UI log for visibility without spamming
      if (total !== null && processed !== null) {
        addLog(`Element progress ${processed}/${total}${msg.symbol ? ` (${msg.symbol})` : ''}`, 'info');
      }

      setElementProgress({
        processed,
        total,
        symbol: msg.symbol || null,
        ts: msg.ts || Date.now(),
        deviceId: msg.deviceId,
      });
    });
    return () => {
      socket.off('device-feedback');
      socket.off('device-ack');
      socket.off('ota-status');
      socket.off('element-progress');
    };
  }, [selectedDeviceId]);

  // Clear stale feedback when switching devices
  useEffect(() => {
    setFeedbackInfo(null);
  }, [selectedDeviceId]);
  // --- FEEDBACK/ADC/STATISTICS REQUEST HANDLERS ---
  // Removed unused handleGetFeedbackInfo function
  // Removed handleGetADCData and handleGetStatData (no longer used)

  // Removed filteredElements and searchTerm logic (already deleted)

  // Utility and handler functions used in JSX
  const getSelectedElementsFromChecklist = () => {
    return elementChecklist
      .filter(item => item.checked)
      .map(item => ({
        symbol: item.symbol,
        name: item.name,
        quantity: item.quantity,
        vout_base: item.vout_base,
        freq: item.freq
      }));
  };

  const handleChecklistChange = (symbol, checked) => {
    setElementChecklist(prev => {
      // First update the checked status
      const updatedChecklist = prev.map(item =>
        item.symbol === symbol ? { ...item, checked } : item
      );
      
      // Then sort: checked items first, then unchecked items
      return updatedChecklist.sort((a, b) => {
        if (a.checked && !b.checked) return -1;
        if (!a.checked && b.checked) return 1;
        return 0; // Keep original order within each group
      });
    });
  };

  // Removed unused: handleChecklistQuantityChange (already deleted)

  const handleCustomQtyChange = (symbol, quantity) => {
    setElementChecklist(prev =>
      prev.map(item =>
        item.symbol === symbol ? { ...item, quantity: Number(quantity) } : item
      )
    );
  };

  const handleTestSubmit = async (e) => {
    e.preventDefault();
    console.log('🔍 handleTestSubmit called');

    if (!selectedDeviceId) {
      setAlert({ type: 'error', message: 'Please select a device before sending parameters.' });
      addLog('Send parameters blocked: no device selected', 'error');
      return;
    }

    const selectedFromChecklist = getSelectedElementsFromChecklist();
    if (selectedFromChecklist.length === 0) {
      setAlert({ type: 'error', message: 'Please select at least one element from the checklist' });
      addLog('Send parameters blocked: no elements selected', 'error');
      return;
    }
    try {
      setLoading(true);
      const testData = selectedFromChecklist.map(element => ({
        symbol: element.symbol,
        quantity: element.quantity
      }));

      console.log('🔍 Sending test data:', testData);
      console.log('🔍 API call to /device/sw-parameters');

      const response = await api.post('/device/sw-parameters', {
        elements: testData,
        deviceId: selectedDeviceId // Always include deviceId in request body
      });
      console.log('🔍 API response received:', response);

      const result = response?.data;
      const sent = !!(result && result.sent);
      if (sent) {
        setAlert({ type: 'success', message: 'Test parameters sent successfully!' });
        addLog('Test parameters sent successfully', 'success');
        setElementChecklist(prev => prev.map(item => ({ ...item, checked: false })));
      } else {
        const msg = result?.error || 'Device not connected';
        setAlert({ type: 'error', message: `Failed to send parameters: ${msg}` });
        addLog(`Failed to send parameters: ${msg}`, 'error');
      }
    } catch (error) {
      console.error('🔍 API call failed:', error);
      console.error('🔍 Error details:', error.response?.data, error.response?.status);
      setAlert({ type: 'error', message: 'Failed to send test parameters' });
      addLog('Failed to send test parameters', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeviceSettingsEdit = async (rowIndex, columnId, value) => {
    const updatedSettings = { ...deviceSettings };
    const settingKey = Object.keys(updatedSettings).filter(key => key !== 'vout_table')[rowIndex];
    if (settingKey) {
      updatedSettings[settingKey] = value;
      setDeviceSettings(updatedSettings);
      try {
  await api.post('/device/settings', {
          ...updatedSettings,
          deviceId: selectedDeviceId
        });
        setAlert({ type: 'success', message: 'Device settings updated successfully!' });
      } catch (error) {
        setAlert({ type: 'error', message: 'Failed to update device settings' });
      }
    }
  };

  // Fetch device settings on-demand (sends CMD_GET_DEVICE_SETTINGS via backend)
  const handleGetDeviceSettings = async () => {
    console.log('🔍 handleGetDeviceSettings called, selectedDeviceId:', selectedDeviceId);
    
    if (!selectedDeviceId) {
      setAlert({ type: 'error', message: 'Please select a device first' });
      return;
    }
    
    try {
      setSettingsLoading(true);
      console.log('🔍 API Request: GET /device/settings', selectedDeviceId);
      
      // Use GET request with deviceId as query parameter and 10-second timeout
      const res = await api.get('/device/settings', { 
        params: { deviceId: selectedDeviceId },
        timeout: 10000 // 10 second timeout
      });
      
      console.log('✅ Device settings response:', res.data);
      // Expecting a structured object with settings
      if (res && res.data) {
        // Check if it's a timeout/pending response
        if (res.data.status === 'pending') {
          setAlert({ type: 'warning', message: res.data.message || 'Device did not respond in time' });
        } else {
          setDeviceSettings(res.data);
          setAlert({ type: 'success', message: 'Device settings fetched successfully' });
            addLog('Device settings fetched successfully', 'success');
        }
      } else {
        setAlert({ type: 'warning', message: 'No settings returned from device' });
          addLog('No settings returned from device', 'warning');
      }
    } catch (error) {
      console.error('❌ Failed to fetch device settings:', error);
      
      if (error.code === 'ECONNABORTED') {
        setAlert({ type: 'error', message: 'Request timeout - device did not respond' });
      } else {
        setAlert({ type: 'error', message: error?.response?.data?.error || error?.response?.data?.message || 'Failed to fetch device settings' });
        addLog('Failed to fetch device settings', 'error');
      }
    } finally {
      setSettingsLoading(false);
    }
  };

  // Excel import/export functions for STP using utility functions
  const exportSTP = () => {
    try {
      // Prepare STP data with quantities from checklist
      const selectedFromChecklist = getSelectedElementsFromChecklist();
      
      if (selectedFromChecklist.length === 0) {
        setAlert({ type: 'error', message: 'Please select elements from the checklist to export' });
        return;
      }

      const stpData = selectedFromChecklist.map(element => ({
        symbol: element.symbol,
        name: element.name,
        quantity: element.quantity,
        vout_base: element.vout_base,
        freq: element.freq
      }));

      const result = exportComprehensiveData(deviceSettings, stpData, 'stp_and_device_settings.xlsx', {
        includeValidation: true,
        timestamp: new Date().toISOString()
      });

      if (result.success) {
        setAlert({ type: 'success', message: 'STP and device settings exported successfully!' });
        addLog('Exported STP and device settings', 'success');
      } else {
        setAlert({ type: 'error', message: `Export failed: ${result.message}` });
        addLog(`Export failed: ${result.message}`, 'error');
      }
    } catch (error) {
      console.error('Export error:', error);
      setAlert({ type: 'error', message: 'Failed to export STP parameters' });
      addLog('Failed to export STP parameters', 'error');
    }
  };

  const importSTP = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setLoading(true);
      
      const result = await importComprehensiveData(file, {
        validateData: true,
        allowUnknownParameters: false
      });

      if (result.success) {
        // Update STP data in checklist
        if (result.stpData && result.stpData.length > 0) {
          const updatedChecklist = elementChecklist.map(checklistItem => {
            const importedData = result.stpData.find(row => row.symbol === checklistItem.symbol);
            if (importedData) {
              return {
                ...checklistItem,
                quantity: Number(importedData.quantity) || checklistItem.defaultQuantity,
                checked: true
              };
            }
            return checklistItem;
          });
          
          setElementChecklist(updatedChecklist);
        }

        // Update device settings
        if (result.deviceSettings && Object.keys(result.deviceSettings).length > 0) {
          setDeviceSettings(prev => ({ ...prev, ...result.deviceSettings }));
        }

        // Show validation warnings if any
        if (result.warnings && result.warnings.length > 0) {
          setAlert({ 
            type: 'warning', 
            message: `Import completed with warnings: ${result.warnings.join(', ')}` 
          });
          addLog('Import completed with warnings', 'warning');
        } else {
          setAlert({ type: 'success', message: 'STP and device settings imported successfully!' });
          addLog('STP and device settings imported successfully', 'success');
        }

        // Show validation summary if available
        if (result.validation) {
          console.log('Import validation:', result.validation);
        }
      } else {
        setAlert({ type: 'error', message: `Import failed: ${result.message}` });
        addLog(`Import failed: ${result.message}`, 'error');
      }
    } catch (error) {
      console.error('Import error:', error);
      setAlert({ type: 'error', message: 'Failed to import STP parameters. Please check the file format.' });
      addLog('Failed to import STP parameters', 'error');
    } finally {
      setLoading(false);
      event.target.value = '';
    }
  };

  const downloadSTPTemplate = () => {
    try {
      const result = createComprehensiveTemplate('stp_and_device_template.xlsx', {
        includeInstructions: true,
        includeValidationRules: true
      });

      if (result.success) {
        setAlert({ type: 'success', message: 'STP and device settings template downloaded successfully!' });
        addLog('Downloaded STP template', 'success');
      } else {
        setAlert({ type: 'error', message: `Template download failed: ${result.message}` });
        addLog(`Template download failed: ${result.message}`, 'error');
      }
    } catch (error) {
      console.error('Template download error:', error);
      setAlert({ type: 'error', message: 'Failed to download template' });
      addLog('Failed to download template', 'error');
    }
  };

  // Removed exportSWPForDevice, exportControllerSWP, and related orphaned code (already deleted)

  // Available elements data with default quantities
  const ELEMENTS = useMemo(() => [
  { symbol: 'Li', name: 'Li', defaultQuantity: 100, vout_base: 3.71, freq: 2226 },
  { symbol: 'Ca', name: 'Ca', defaultQuantity: 100, vout_base: 2.95, freq: 1765 },
  { symbol: 'Na', name: 'Na', defaultQuantity: 100, vout_base: 3.032, freq: 1818 },
  { symbol: 'Cl', name: 'Cl', defaultQuantity: 100, vout_base: 3.916, freq: 2351 },
  { symbol: 'Fe', name: 'Fe', defaultQuantity: 100, vout_base: 4.558, freq: 2739 },
  { symbol: 'Zn', name: 'Zn', defaultQuantity: 100, vout_base: 4.167, freq: 2504 },
  { symbol: 'Cu', name: 'Cu', defaultQuantity: 100, vout_base: 4.557, freq: 2739 },
  { symbol: 'Pb', name: 'Pb', defaultQuantity: 100, vout_base: 3.321, freq: 1988 },
  { symbol: 'Mg', name: 'Mg', defaultQuantity: 100, vout_base: 3.63, freq: 2175 },
  { symbol: 'Mn', name: 'Mn', defaultQuantity: 100, vout_base: 4.497, freq: 2697 },
  { symbol: 'Cd', name: 'Cd', defaultQuantity: 100, vout_base: 3.711, freq: 2228 },
  { symbol: 'K', name: 'K', defaultQuantity: 100, vout_base: 2.454, freq: 1466 },
  { symbol: 'B', name: 'B', defaultQuantity: 100, vout_base: 5.256, freq: 3161 },
  { symbol: 'F', name: 'F', defaultQuantity: 100, vout_base: 3.896, freq: 2340 },
  { symbol: 'Mo', name: 'Mo', defaultQuantity: 100, vout_base: 4.147, freq: 2486 },
  { symbol: 'Ni', name: 'Ni', defaultQuantity: 100, vout_base: 4.661, freq: 2797 },
  { symbol: 'Se', name: 'Se', defaultQuantity: 100, vout_base: 3.423, freq: 2052 },
  { symbol: 'Si', name: 'Si', defaultQuantity: 100, vout_base: 3.814, freq: 2287 },
  { symbol: 'Ag', name: 'Ag', defaultQuantity: 100, vout_base: 4.043, freq: 2428 },
  { symbol: 'As', name: 'As', defaultQuantity: 100, vout_base: 3.711, freq: 2228 },
  { symbol: 'Hg', name: 'Hg', defaultQuantity: 100, vout_base: 3.568, freq: 2140 },
  { symbol: 'P', name: 'P', defaultQuantity: 100, vout_base: 3.402, freq: 2041 },
  { symbol: 'Al', name: 'Al', defaultQuantity: 100, vout_base: 4.065, freq: 2439 },
  { symbol: 'Cr', name: 'Cr', defaultQuantity: 100, vout_base: 1.343, freq: 797 },
  { symbol: 'Co', name: 'Co', defaultQuantity: 100, vout_base: 4.66, freq: 2797 },
  { symbol: 'Ba', name: 'Ba', defaultQuantity: 100, vout_base: 2.598, freq: 1554 },
  { symbol: 'Am', name: 'Am', defaultQuantity: 100, vout_base: 3.341, freq: 1999 },
  { symbol: 'NO', name: 'NO', defaultQuantity: 100, vout_base: 4.660, freq: 2797 }
  ], []);

  const canViewVout = role !== 'user' || allowUserVoutView;

  const elementChecklistColumns = useMemo(() => {
    const cols = [
      { id: 'checked', label: '', align: 'center', sortable: false, render: (val, row) => (
        <input type="checkbox" checked={row.checked} onChange={e => handleChecklistChange(row.symbol, e.target.checked)} />
      ) },
      { id: 'symbol', label: 'Symbol', sortable: true },
      { id: 'name', label: 'Name', sortable: true },
      { id: 'quantity', label: 'Quantity', sortable: true, editable: true, type: 'number', inputProps: { min: 0 }, render: (val, row) => (
        <input type="number" value={row.quantity} min={0} style={{ width: 60, background: '#fff', color: '#222', border: '1px solid #ccc', borderRadius: 4 }} onChange={e => handleCustomQtyChange(row.symbol, e.target.value)} />
      ) }
    ];
    if (canViewVout) {
      cols.push({ id: 'vout_base', label: 'Vout Base', sortable: true });
      cols.push({ id: 'freq', label: 'Freq', sortable: true });
    }
    return cols;
  }, [canViewVout]);

  const selectedElementsColumns = useMemo(() => {
    const cols = [
      { id: 'symbol', label: 'Symbol', sortable: true },
      { id: 'name', label: 'Name', sortable: true },
      { id: 'quantity', label: 'Quantity', sortable: true },
    ];
    if (canViewVout) {
      cols.push({ id: 'vout_base', label: 'Vout Base', sortable: true });
      cols.push({ id: 'freq', label: 'Freq', sortable: true });
    }
    return cols;
  }, [canViewVout]);



  // Column definitions for Device Settings - non-editable for non-admin users
  const deviceSettingsColumns = [
    { id: 'setting', label: 'Setting', sortable: true, editable: false },
    { 
      id: 'value', 
      label: 'Value', 
      align: 'center', 
      sortable: true,
      editable: role === 'admin' || role === 'superadmin',
      type: 'number',
      inputProps: { min: 0 }
    },
    { 
      id: 'unit', 
      label: 'Unit', 
      align: 'center', 
      sortable: true,
      editable: false
    }
  ];



  // Fetch device settings
  // Removed unused fetchDeviceSettings helper

  // Removed fetchTransportMode and handleTransportModeChange (no longer used)

  useEffect(() => {
    const socket = getSocket();
    socket.on('device-settings-updated', (newSettings) => {
      setDeviceSettings(newSettings);
    });
    return () => {
      socket.off('device-settings-updated');
    };
  }, []);

  // Initialize element checklist with all elements
  useEffect(() => {
    const checklist = ELEMENTS.map(element => ({
      ...element,
      checked: false,
      quantity: element.defaultQuantity
    }));
    setElementChecklist(checklist);
  }, [ELEMENTS]);



  const activeDevice = deviceOptions.find(o => o.id === selectedDeviceId);
  // DEVICE (STM32) firmware version reported over WS; ESP bridge version is separate.
  const deviceFirmware = activeDevice?.stmFirmwareVersion || null;
  const espBridgeVersion = activeDevice?.espBridgeVersion || null;

  // Convert device settings to table data
  const deviceSettingsData = [
    { setting: 'Device ID', value: deviceSettings.device_id || selectedDeviceId || 'N/A', unit: '' },
    { setting: 'Device Firmware', value: deviceFirmware || 'unknown', unit: activeDevice?.buildCode != null ? `build ${activeDevice.buildCode}` : '' },
    ...(espBridgeVersion ? [{ setting: 'Bridge version', value: espBridgeVersion, unit: '' }] : []),
    { setting: 'Operating Mode', value: deviceSettings.operating_mode || transportMode, unit: '' },
    { setting: 'Freefall', value: deviceSettings.freefall !== undefined ? deviceSettings.freefall : 10, unit: 'ms' },
    { setting: 'HPTF', value: deviceSettings.hptf !== undefined ? deviceSettings.hptf : 500, unit: 'Hz' },
    { setting: 'Harmonic', value: deviceSettings.harmonic || 'FULL', unit: '' },
    { setting: 'Duration', value: deviceSettings.duration_ms !== undefined ? deviceSettings.duration_ms : 7000, unit: 'ms' }
  ];

  const isAdmin = role === 'admin' || role === 'superadmin';

  const showOta = selectedDeviceId && otaStatus && (otaStatus.progress !== null || otaStatus.message);
  const activeDeviceName = activeDevice?.name;
  const activeLabel = activeDeviceName && activeDeviceName !== selectedDeviceId
    ? `Active: ${activeDeviceName} (${selectedDeviceId})`
    : `Active: ${selectedDeviceId}`;

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: 'auto' }}>
      {/* Header */}
      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} justifyContent="space-between" spacing={1} sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Device Control</Typography>
        {selectedDeviceId && (
          <Chip size="small" variant="outlined" color="primary" label={activeLabel} />
        )}
      </Stack>

      {alert && (
        <Alert severity={alert.type} sx={{ mb: 2 }} onClose={() => setAlert(null)}>
          {alert.message}
        </Alert>
      )}

      {/* Device + transport controls */}
      <SectionCard title="Device & Transport" sx={{ mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} flexWrap="wrap" useFlexGap>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Device</InputLabel>
            <Select label="Device" value={selectedDeviceId || ''} onChange={(e) => setSelectedDeviceId(e.target.value)}>
              <MenuItem value=""><em>Select a device</em></MenuItem>
              {deviceOptions.map(opt => (<MenuItem key={opt.id} value={opt.id}>{opt.name}</MenuItem>))}
            </Select>
          </FormControl>
          <Divider flexItem orientation="vertical" sx={{ display: { xs: 'none', md: 'block' } }} />
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="body2" color="text.secondary">Transport</Typography>
            <ButtonGroup variant="outlined" size="small" disabled={transportLoading}>
              {['auto', 'wifi', '4g', 'uart', 'bluetooth'].map((mode) => (
                <Button
                  key={mode}
                  variant={transportMode === mode ? 'contained' : 'outlined'}
                  onClick={() => handleTransportModeChange(mode)}
                  sx={{ fontWeight: 600, textTransform: mode === '4g' ? 'uppercase' : 'capitalize', minWidth: 72 }}
                  disabled={transportMode === mode || transportLoading}
                >
                  {mode === '4g' ? '4G' : mode}
                </Button>
              ))}
            </ButtonGroup>
            {transportLoading && <Typography variant="body2" color="text.secondary">Switching…</Typography>}
          </Stack>
        </Stack>
      </SectionCard>

      {/* Live progress (only while a device is selected) */}
      {selectedDeviceId && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={showOta ? 6 : 12}>
            <SectionCard title="Element Progress" action={<Typography variant="body2" color="text.secondary">{progressLabel}</Typography>}>
              <LinearProgress variant="determinate" value={progressValue} sx={{ height: 8, borderRadius: 1 }} />
            </SectionCard>
          </Grid>
          {showOta && (
            <Grid item xs={12} md={6}>
              <SectionCard title="STM32 Process Status" action={<Typography variant="body2" color="text.secondary">{otaStatus.progress !== null ? `${otaStatus.progress}%` : '…'}</Typography>}>
                <LinearProgress variant={otaStatus.progress !== null ? 'determinate' : 'indeterminate'} value={otaStatus.progress || 0} sx={{ height: 8, borderRadius: 1 }} />
                {otaStatus.message && <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{otaStatus.message}</Typography>}
              </SectionCard>
            </Grid>
          )}
        </Grid>
      )}

      {/* Main work area: element checklist + device settings */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={7}>
          <SectionCard
            title="Element Checklist"
            action={
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Button size="small" variant="outlined" color="inherit" onClick={exportSTP} startIcon={<DownloadIcon />}>Export STP</Button>
                <Button size="small" variant="outlined" color="inherit" onClick={downloadSTPTemplate} startIcon={<DownloadIcon />}>Template</Button>
                <Button size="small" variant="outlined" color="success" onClick={() => fileInputRef.current.click()} startIcon={<UploadIcon />}>Import STP</Button>
                <input type="file" accept=".xlsx,.xls" ref={fileInputRef} style={{ display: 'none' }} onChange={importSTP} />
              </Stack>
            }
          >
            <SortableTable
              columns={elementChecklistColumns}
              data={elementChecklist}
              showIndex={true}
              showActions={false}
              editable={false}
              defaultRowsPerPage={10}
            />
            <Stack direction="row" spacing={2} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
              <Button variant="contained" onClick={handleTestSubmit} disabled={loading} startIcon={<SendIcon />}>
                Send Test Parameters
              </Button>
              <Button
                variant="outlined"
                onClick={async () => {
                  try {
                    await api.post('/device/ready', { deviceId: selectedDeviceId });
                    setAlert({ type: 'success', message: 'Requested device ready' });
                    addLog('Get Device Ready requested', 'success');
                  } catch (e) {
                    setAlert({ type: 'error', message: e?.response?.data?.error || 'Failed to request device ready' });
                    addLog('Failed to request device ready', 'error');
                  }
                }}
              >
                Get Device Ready
              </Button>
            </Stack>
          </SectionCard>
        </Grid>

        <Grid item xs={12} md={5}>
          <SectionCard title="Selected Elements">
            <SortableTable
              columns={selectedElementsColumns}
              data={elementChecklist.filter(e => e.checked)}
              showIndex={true}
              showActions={false}
              editable={false}
              defaultRowsPerPage={10}
            />
          </SectionCard>
        </Grid>
      </Grid>

      {/* Details: device settings, feedback, console */}
      <Grid container spacing={2} sx={{ mt: 0.5 }}>
        <Grid item xs={12} md={4}>
          <SectionCard
            title="Device Settings"
            action={
              <Button size="small" variant="contained" onClick={handleGetDeviceSettings} disabled={settingsLoading} startIcon={<DownloadIcon />}>
                {settingsLoading ? 'Fetching…' : 'Get Device Settings'}
              </Button>
            }
          >
            <SortableTable
              columns={deviceSettingsColumns}
              data={deviceSettingsData}
              onEdit={handleDeviceSettingsEdit}
              isAdmin={isAdmin}
              editable={isAdmin}
              defaultRowsPerPage={10}
            />
          </SectionCard>
        </Grid>
        <Grid item xs={12} md={4}>
          <SectionCard title="Device Feedback Info">
            {feedbackInfo ? (
              <DeviceFeedbackTable feedback={feedbackInfo} />
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ minHeight: 60 }}>
                No feedback info received yet
              </Typography>
            )}
          </SectionCard>
        </Grid>
        <Grid item xs={12} md={4}>
          <SectionCard title="Console Log" action={<Typography variant="caption" color="text.secondary">latest 5 events</Typography>}>
            {uiLogs.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No events yet.</Typography>
            ) : (
              uiLogs.map((log, idx) => (
                <Stack
                  key={`${log.timestamp}-${idx}`}
                  direction="row" justifyContent="space-between" alignItems="center"
                  sx={{ py: 0.75, borderBottom: idx !== uiLogs.length - 1 ? '1px solid' : 'none', borderColor: 'divider' }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 500, color: log.type === 'error' ? 'error.main' : log.type === 'warning' ? 'warning.dark' : 'text.primary' }}>
                    {log.message}
                  </Typography>
                  <Typography variant="caption" color="text.disabled" sx={{ ml: 2 }}>{log.timestamp}</Typography>
                </Stack>
              ))
            )}
          </SectionCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DeviceControl;
